import { CustomComponentFit } from "./customComponentFit";
import {
    CustomComponentViewerCameraParameters,
    ICustomComponentViewerNavigator,
} from "./customComponentViewerNavigator";
import { ICustomLibraryViewerNavigationEventPayload } from "../../eventBus/customLibraryNavigationEventPayload";
import eventBus, { IApplicationEvent } from "../../eventBus/eventDispatcher";

export class CustomComponentSideViewerNavigator implements ICustomComponentViewerNavigator {
    private readonly initialPosition = new THREE.Vector3();
    private readonly currentPosition = new THREE.Vector3();

    constructor(private readonly viewer: Autodesk.Viewing.GuiViewer3D) {
        this.onCameraChanged = this.onCameraChanged.bind(this);
        this.onConnectedViewerNavigationChanged = this.onConnectedViewerNavigationChanged.bind(this);
    }

    initialize(): void {
        const { position, up } = this.computeCameraParameters();

        this.viewer.navigation.setPosition(position);
        this.viewer.navigation.setCameraUpVector(up);

        this.initialPosition.copy(this.viewer.navigation.getPosition());
        this.currentPosition.copy(this.initialPosition);

        this.viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.onCameraChanged);

        eventBus.addEventListener("Dextall.CustomComponentLibrary.Viewers.Navigation", this.onConnectedViewerNavigationChanged);
    }

    getPlaneX(): THREE.Vector3 {
        return this.getWorldUp().clone().negate();
    }

    getPlaneY(): THREE.Vector3 {
        return this.getWorldFront();
    }

    getPlaneNormal(): THREE.Vector3 {
        return this.getWorldRight();
    }

    dispose(): void {
        this.viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.onCameraChanged);

        eventBus.removeEventListener("Dextall.CustomComponentLibrary.Viewers.Navigation", this.onConnectedViewerNavigationChanged);
    }

    private computeCameraParameters(): CustomComponentViewerCameraParameters {
        const worldFront = this.getWorldFront();
        const worldRight = this.getWorldRight();

        const fitter = new CustomComponentFit(this.viewer);

        const length = Math.abs(worldFront.dot(fitter.computeDistancesToFit()));

        return { position: worldRight.clone().multiplyScalar(length), up: worldFront };
    }

    private onCameraChanged() {
        const currentPosition = this.viewer.navigation.getPosition().clone();

        if (currentPosition.equals(this.currentPosition)) {
            return;
        }

        const targetScale = this.getWorldRight().dot(currentPosition) / this.getWorldRight().dot(this.initialPosition);

        const worldFront = this.getWorldFront();
        const worldUp = this.getWorldUp();

        const planOffset = worldFront.dot(currentPosition);
        const frontOffset = worldUp.dot(currentPosition);

        this.currentPosition.copy(currentPosition);

        eventBus.dispatchEvent({
            type: "Dextall.CustomComponentLibrary.Viewers.Navigation",
            payload: {
                targetViewer: "plan",
                scale: targetScale,
                x: 0,
                y: planOffset,
            },
        });

        eventBus.dispatchEvent({
            type: "Dextall.CustomComponentLibrary.Viewers.Navigation",
            payload: {
                targetViewer: "front",
                scale: targetScale,
                x: 0,
                y: frontOffset,
            },
        });
    }

    private onConnectedViewerNavigationChanged(event: IApplicationEvent<ICustomLibraryViewerNavigationEventPayload>) {
        const { targetViewer, scale, x, y } = event.payload;

        if (targetViewer !== "side") {
            return;
        }

        const right = this.getWorldUp().clone().multiplyScalar(x);
        const up = this.getWorldFront().clone().multiplyScalar(y);

        const position = this.getWorldRight()
            .clone()
            .multiplyScalar(this.initialPosition.length() * scale)
            .add(right)
            .add(up);

        const target = right.add(up);

        this.currentPosition.copy(position);

        this.viewer.navigation.setView(position, target);
    }

    private getWorldFront(): THREE.Vector3 {
        return this.viewer.autocam.getWorldFrontVector();
    }

    private getWorldUp(): THREE.Vector3 {
        return this.viewer.autocam.getWorldUpVector();
    }

    private getWorldRight(): THREE.Vector3 {
        return this.viewer.autocam.getWorldRightVector();
    }
}
